<template>
    <div
      v-if="show"
      class="floating-panel-viewport"
      :class="`floating-panel-viewport--${placement}`"
      :style="viewportStyle"
    >
      <div
        class="floating-panel-shell"
        :style="shellStyle"
        @click.stop
      >
        <div
          class="floating-panel-card"
          role="dialog"
          :aria-label="title"
          :style="panelThemeStyle"
        >
          <!-- 标题栏：左侧图标+名称，右侧折叠/帮助/关闭 -->
          <div
            class="floating-panel-header"
            :class="{ 'floating-panel-header--draggable': draggable }"
            @pointerdown="dragController.handlePointerDown"
          >
            <div class="floating-panel-header__leading">
              <slot name="icon">
                <SvgIcon
                  v-if="icon"
                  class="floating-panel-header__icon"
                  :content="icon"
                  :alt="`${title}图标`"
                />
              </slot>
              <span class="floating-panel-header__title">{{ title }}</span>
            </div>

            <div class="floating-panel-header__actions">
              <slot name="header-actions" />

              <n-button
                v-if="showCollapse"
                quaternary
                size="tiny"
                circle
                :title="collapsed ? '展开' : '折叠'"
                @click.stop="handleToggleCollapsed"
              >
                <template #icon>
                  <n-icon>
                    <ChevronUp v-if="collapsed" />
                    <ChevronDown v-else />
                  </n-icon>
                </template>
              </n-button>

              <n-button
                v-if="showHelp"
                quaternary
                size="tiny"
                circle
                title="帮助"
                @click.stop="handleHelp"
              >
                <template #icon>
                  <n-icon><Help /></n-icon>
                </template>
              </n-button>

              <n-button
                quaternary
                size="tiny"
                circle
                title="关闭"
                @click.stop="handleClose"
              >
                <template #icon>
                  <n-icon><Close /></n-icon>
                </template>
              </n-button>
            </div>
          </div>

          <!-- 内容区：由业务场景插入具体子组件 -->
          <div
            v-show="!collapsed"
            class="floating-panel-body"
          >
            <slot />
          </div>

          <div
            v-if="$slots.footer && !collapsed"
            class="floating-panel-footer"
          >
            <slot name="footer" />
          </div>
        </div>
      </div>
    </div>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue';
import {
  NButton,
  NIcon,
  useThemeVars,
} from 'naive-ui';
import {
  ChevronDown,
  ChevronUp,
  Close,
  Help,
} from '@vicons/carbon';
import {
  useFloatingDialogDrag,
  type FloatingPanelPlacement,
  type FloatingPanelViewportInset,
} from '@/composables/dialog/useFloatingDialogDrag';
import { provideFloatingPanelOverlayContext } from '@/composables/dialog/useFloatingPanelOverlayContext';
import SvgIcon from '@/components/common/SvgIcon.vue';

/** 折叠后仅保留标题栏，用于拖拽边界钳制。 */
const COLLAPSED_PANEL_ESTIMATED_HEIGHT = 36;

/**
 * 通用浮动面板属性。
 */
interface FloatingPanelProps {
  /** 面板是否可见。 */
  show: boolean;
  /** 面板标题。 */
  title: string;
  /** 标题左侧图标 SVG 原始标记（由 `@/assets/icon/xx.svg?raw` 导入）；也可通过 icon 插槽自定义。 */
  icon?: string;
  /** 面板宽度（px）。 */
  width?: number;
  /** 面板预估高度（px），用于拖拽边界钳制。 */
  estimatedHeight?: number;
  /** 是否折叠内容区，仅保留标题栏。 */
  collapsed?: boolean;
  /** 是否显示折叠按钮。 */
  showCollapse?: boolean;
  /** 是否显示帮助按钮。 */
  showHelp?: boolean;
  /** 标题栏是否可拖动 reposition。 */
  draggable?: boolean;
  /** 每次重新显示时是否重置到默认锚点位置。 */
  resetPositionOnShow?: boolean;
  /** 默认锚点位置。 */
  placement?: FloatingPanelPlacement;
  /** 左上角锚点时的初始内边距（拖拽钳制按整页视口计算）。 */
  viewportInset?: FloatingPanelViewportInset;
  /** 视口层 z-index。 */
  zIndex?: number;
}

const props = withDefaults(defineProps<FloatingPanelProps>(), {
  icon: undefined,
  width: 320,
  estimatedHeight: 260,
  collapsed: false,
  showCollapse: true,
  showHelp: true,
  draggable: true,
  resetPositionOnShow: true,
  placement: 'center',
  viewportInset: () => ({ top: 280, left: 100 }),
  zIndex: 4000,
});

const emit = defineEmits<{
  /** 关闭面板。 */
  close: [];
  /** 点击帮助按钮。 */
  help: [];
  /** 同步折叠状态。 */
  'update:collapsed': [collapsed: boolean];
  /** 同步可见状态。 */
  'update:show': [show: boolean];
}>();

/**
 * 向子树提供 overlay z-index，供 DropdownButton 等弹出层组件抬升层级。
 */
provideFloatingPanelOverlayContext(computed(() => props.zIndex));

/**
 * Naive UI 主题变量。
 *
 * 设计说明：
 * 1. common 主题字段（modalColor 等）不会自动变成 .n-config-provider 上的 --n-modal-color；
 * 2. 自定义组件应通过 useThemeVars 读取 JS 主题值，再写入 inline style；
 * 3. 这与 n-card 等内置组件在根节点注入 --n-color-modal 的机制不同，但数据源相同。
 */
const themeVars = useThemeVars();

/**
 * 浮动面板表面样式（背景/边框/文字），随亮暗主题与 themeOverrides 响应更新。
 */
const panelThemeStyle = computed<Record<string, string>>(() => ({
  backgroundColor: themeVars.value.modalColor,
  color: themeVars.value.textColor2,
  border: `1px solid ${themeVars.value.borderColor}`,
  '--floating-panel-divider-color': themeVars.value.dividerColor,
}));

/**
 * 面板拖拽控制器。
 *
 * 设计说明：拖拽逻辑统一收口到 composable，面板只声明尺寸与标题栏交互规则。
 */
const dragController = useFloatingDialogDrag({
  width: computed(() => props.width),
  estimatedHeight: computed(() => (
    props.collapsed ? COLLAPSED_PANEL_ESTIMATED_HEIGHT : props.estimatedHeight
  )),
  draggable: computed(() => props.draggable),
  placement: computed(() => props.placement),
  viewportInset: computed(() => props.viewportInset),
  shouldStartDrag: (event) => {
    if (!(event.target instanceof Element)) {
      return true;
    }
    /**
     * 点击标题栏右侧按钮时不应触发拖拽。
     */
    return event.target.closest('.floating-panel-header__actions') === null;
  },
});

/**
 * 面板 shell 样式。
 *
 * 设计说明：必须在 setup 顶层暴露 ComputedRef，模板才能响应拖拽偏移更新。
 */
const shellStyle = dragController.shellStyle;

/**
 * 视口容器样式。
 */
const viewportStyle = computed<Record<string, string>>(() => {
  const style: Record<string, string> = {
    zIndex: String(props.zIndex),
  };

  if (props.placement === 'top-left') {
    style.paddingTop = `${props.viewportInset.top}px`;
    style.paddingLeft = `${props.viewportInset.left}px`;
  }

  return style;
});

/**
 * 切换折叠状态。
 */
function handleToggleCollapsed(): void {
  emit('update:collapsed', !props.collapsed);
}

/**
 * 触发帮助事件。
 */
function handleHelp(): void {
  emit('help');
}

/**
 * 关闭面板并同步可见状态。
 */
function handleClose(): void {
  emit('update:show', false);
  emit('close');
}

/**
 * 面板重新打开时回到默认位置，避免上次拖动位置影响下一次使用。
 */
watch(
  () => props.show,
  () => {
    if (!props.resetPositionOnShow) {
      return;
    }
    dragController.resetPosition();
  },
);
</script>

<style scoped lang="less">
.floating-panel {
  &-viewport {
    position: fixed;
    inset: 0;
    display: flex;
    pointer-events: none;

    &--center {
      align-items: center;
      justify-content: center;
    }

    &--top-left {
      align-items: flex-start;
      justify-content: flex-start;
    }
  }

  &-shell {
    pointer-events: auto;
  }

  &-card {
    display: flex;
    flex-direction: column;
    border-radius: 6px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
    overflow: hidden;
  }

  &-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    min-height: 36px;
    padding: 4px 8px 4px 10px;
    border-bottom: 1px solid var(--floating-panel-divider-color);
    user-select: none;

    &--draggable {
      cursor: move;
    }

    &__leading {
      display: flex;
      align-items: center;
      gap: 8px;
      min-width: 0;
      flex: 1;
    }

    &__icon {
      width: 18px;
      height: 18px;
      flex-shrink: 0;
    }

    &__title {
      font-size: 14px;
      font-weight: 600;
      line-height: 20px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    &__actions {
      display: inline-flex;
      align-items: center;
      gap: 2px;
      flex-shrink: 0;
    }
  }

  &-body {
    padding: 12px;
    max-height: min(70vh, 520px);
    overflow: auto;
  }

  &-footer {
    padding: 8px 12px 12px;
    border-top: 1px solid var(--floating-panel-divider-color);
  }
}
</style>
